import React, { useMemo } from 'react';
import { PrismTypeahead } from '@prism-ui/react';

/**
 * Single-select user picker on prism-typeahead. Keeps the react-bootstrap-typeahead
 * contract its callers were written against: `selected` is an array of option objects
 * and `onChange` receives `[item]` on selection or `[]` when cleared.
 */
const UserTypeahead = ({
  id,
  label = 'Name',
  hideLabel = false,
  labelKey = 'name',
  options = [],
  selected = [],
  placeholder = '',
  onChange,
}) => {
  const selectedItem = selected && selected.length > 0 ? selected[0] : null;
  const selectedLabel = selectedItem ? String(selectedItem[labelKey] ?? '') : '';

  const keyOf = (option, index) => String(option.id ?? index);
  const prismOptions = useMemo(
    () => options.map((option, index) => ({ label: String(option[labelKey] ?? ''), value: keyOf(option, index) })),
    [options, labelKey]
  );

  const handleChange = (e) => {
    const detail = e.detail ?? {};
    if (detail.name === 'single-value-selected') {
      const index = options.findIndex((option, i) => keyOf(option, i) === String(detail.value));
      if (index !== -1) onChange([options[index]]);
      return;
    }
    // Typing (or the clear control) after a pick drops the selection, as the old input did.
    // Ignore the echo prism-typeahead emits when we set its value to the selected label.
    if (selectedItem && String(detail.value ?? '') !== selectedLabel) onChange([]);
  };

  return (
    <PrismTypeahead
      id={id}
      className="user-typeahead"
      label={label}
      hideLabel={hideLabel}
      as="single"
      search="include"
      start={1}
      options={prismOptions}
      value={selectedLabel}
      placeholder={placeholder}
      onPrismChange={handleChange}
    />
  );
};

export default UserTypeahead;
